/**
 * /careers — public page.
 *
 * Three sections:
 *   1. Hero — who we look for
 *   2. What it's like on the floor (typical day, growth path)
 *   3. Apply form (internship vs coach toggle, structured intake, anti-bot)
 *
 * We're honest in the ack email + on this page that we may not be hiring
 * right now — applicants stay on file and get pinged when a spot opens.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
    Award,
    Clock,
    GraduationCap,
    Linkedin,
    Loader2,
    Send,
    Sparkles,
    Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COACH_CERT_OPTIONS = [
    "CSCS", "NASM-CPT", "ACE-CPT", "ACSM-CPT", "NSCA-CPT",
    "USAW", "FMS", "RPR", "PRI", "CPR/AED",
];

export default function Careers() {
    const [options, setOptions] = useState(null);
    const [openedAt] = useState(() => Date.now());

    useEffect(() => {
        axios.get(`${API}/coach-applications/options`)
            .then((r) => setOptions(r.data))
            .catch(() => setOptions({ cue_options: [], philosophy_questions: [] }));
    }, []);

    return (
        <div data-testid="careers-page" className="bg-brand-obsidian text-white min-h-screen">
            <PageHero />
            <FloorSection />
            <GrowthSection />
            <section id="apply" className="py-16 md:py-20 border-t border-zinc-900 bg-gradient-to-b from-black to-zinc-950/60">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" /> Apply
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl tracking-wider uppercase">
                        Tell us about yourself.
                    </h2>
                    <p className="mt-4 text-zinc-300 text-sm md:text-base leading-relaxed">
                        We don't always have an open spot. Coaching at MaxSC is small, high-trust,
                        and we only bring people on when we know we can pour into them.{" "}
                        <span className="text-white">If we're not hiring right now, your application stays on file —
                        when a spot opens that fits, we'll email you directly.</span>
                    </p>
                    <ApplicationForm options={options} openedAt={openedAt} />
                </div>
            </section>
        </div>
    );
}

function PageHero() {
    return (
        <section className="relative pt-36 pb-16 border-b border-zinc-900 overflow-hidden">
            <div className="absolute inset-0 bg-grid-faint opacity-30 pointer-events-none" />
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-red/15 via-transparent to-transparent pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-6 md:px-10">
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-3">
                    <span className="inline-block w-10 h-px bg-brand-red" /> Careers
                </div>
                <h1 className="font-display text-5xl md:text-7xl tracking-wider leading-[0.95] uppercase">
                    Coach with us.<br />
                    <span className="text-brand-red">Train like one.</span>
                </h1>
                <p className="mt-7 text-zinc-300 text-base md:text-lg max-w-2xl leading-relaxed">
                    We're looking for coaches and interns who actually like the work — the science,
                    the floor time, the long-term athlete relationships. The kind of people who read
                    training research for fun and can teach an 11-year-old to squat without losing
                    their patience.
                </p>
            </div>
        </section>
    );
}

function FloorSection() {
    return (
        <section className="py-16 md:py-20 border-b border-zinc-900">
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> The role
                </div>
                <h2 className="font-display text-3xl md:text-5xl tracking-wider uppercase mb-10 max-w-3xl">
                    What it actually looks like.
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                    <DayCard
                        icon={Clock}
                        title="Your schedule"
                        text="Hours vary by your assigned schedule. We build each coach's week around the athletes and clients they're responsible for — early mornings, mid-day, after-school, or evenings depending on the roster you take on."
                    />
                    <DayCard
                        icon={Users}
                        title="Who you coach"
                        text="Every coach at MaxSC works with both adults and athletes. Same standard, same care — whether it's a 12-year-old learning to squat or a 45-year-old chasing their first pull-up. You'll be expected to coach both."
                    />
                </div>
                <p className="mt-10 text-zinc-300 text-sm leading-relaxed max-w-2xl">
                    You're not running classes off a script. You're coaching individuals through programs we
                    build together. Expect to teach, learn, write, lift, and grow.
                </p>
            </div>
        </section>
    );
}

function GrowthSection() {
    return (
        <section className="py-16 md:py-20 border-b border-zinc-900 bg-zinc-950/30">
            <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                        <Award className="w-3.5 h-3.5" /> Growth & support
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl tracking-wider uppercase mb-6">
                        We invest in you.
                    </h2>
                    <ul className="space-y-3 text-zinc-300 text-sm md:text-base leading-relaxed">
                        <li className="flex items-start gap-3">
                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                            Cert support — we'll help you pursue CSCS, RPR, USAW, NASM. Bring the work, we'll help cover the path.
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                            Weekly coaching huddles — case reviews, programming critiques, the stuff most gyms skip.
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                            Internship to coach pathway — interns who put in real reps get first look at full roles.
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="text-brand-red mt-2 w-1 h-1 rounded-full bg-brand-red shrink-0" />
                            Tech that pays attention — every program, max, and assessment is logged, so you actually see compounding progress on your athletes.
                        </li>
                    </ul>
                </div>
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                        <GraduationCap className="w-3.5 h-3.5" /> Who fits
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl tracking-wider uppercase mb-6">
                        And who doesn't.
                    </h2>
                    <div className="grid grid-cols-2 gap-6 text-sm">
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-navy-300 mb-3">Yes</div>
                            <ul className="space-y-2.5 text-zinc-300 leading-relaxed">
                                <li>Invests in their education — courses, certs, mentors, books. Not just YouTube.</li>
                                <li>Trains themselves — weight training non-negotiable. You can't coach the work if you don't do the work.</li>
                                <li>Early to prep — if shift starts at 3, they're in by 2:30.</li>
                                <li>Coaches all ages with the same level of professionalism and care.</li>
                                <li>Pursuit of excellence on and off the floor.</li>
                                <li>Handles stress like an adult. Team player. No ego.</li>
                                <li>Always trying to get better at every part of the job.</li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-3">No</div>
                            <ul className="space-y-2.5 text-zinc-400 leading-relaxed">
                                <li>Education ends at YouTube, Instagram, and free resources.</li>
                                <li>Parrots what they heard on social without doing the reading.</li>
                                <li>Doesn't train themselves — especially with weights.</li>
                                <li>Shows up at 2:59 thinking they're "on time."</li>
                                <li>Low EQ — can't read a room, can't take feedback.</li>
                                <li>Dishonest. Late. Low integrity.</li>
                                <li>Treats coaching as a stepping stone, not a craft.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function DayCard({ icon: Icon, title, text }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35 }}
            className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-6"
        >
            <Icon className="w-5 h-5 text-brand-red mb-3" />
            <div className="font-display text-2xl tracking-wider text-white mb-2 uppercase">{title}</div>
            <p className="text-zinc-300 text-sm leading-relaxed">{text}</p>
        </motion.div>
    );
}

// -------------------------- Form --------------------------

function ApplicationForm({ options, openedAt }) {
    const [role, setRole] = useState("coach");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone: "",
        linkedin_url: "", resume_url: "",
        why_maxsc: "", earliest_start: "",
        years_experience: "", current_employer: "", availability: "",
        school: "", grad_year: "", hours_per_week: "", compensation_pref: "",
        scenario_response: "",
        company: "", // honeypot
    });
    const [certs, setCerts] = useState([]);
    const [philosophy, setPhilosophy] = useState({});
    const [cues, setCues] = useState([]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleCert = (c) => setCerts((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
    const toggleCue = (c) => setCues((p) => {
        if (p.includes(c)) return p.filter((x) => x !== c);
        if (p.length >= 3) {
            toast.info("Pick your top 3 cues");
            return p;
        }
        return [...p, c];
    });

    const philosophyQs = options?.philosophy_questions || [];
    const cueOptions = options?.cue_options || [];

    const canSubmit = useMemo(() => {
        const baseOk =
            form.first_name.trim() && form.last_name.trim() &&
            form.email.includes("@") && form.phone.trim().length >= 7 &&
            form.linkedin_url.trim().length >= 8 &&
            form.why_maxsc.trim().length >= 20;
        if (!baseOk) return false;
        if (role === "internship") {
            return form.school.trim() && form.grad_year && form.hours_per_week;
        }
        return true;
    }, [form, role]);

    const submit = async () => {
        if (!canSubmit) {
            toast.error("Please fill in the required fields");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                role_interest: role,
                current_certs: role === "coach" ? certs : undefined,
                years_experience: form.years_experience ? Number(form.years_experience) : undefined,
                grad_year: form.grad_year ? Number(form.grad_year) : undefined,
                hours_per_week: form.hours_per_week ? Number(form.hours_per_week) : undefined,
                availability: form.availability || undefined,
                compensation_pref: form.compensation_pref || undefined,
                philosophy: Object.keys(philosophy).length ? philosophy : undefined,
                cueing_picks: cues.length ? cues : undefined,
                form_opened_at: openedAt,
            };
            const r = await axios.post(`${API}/coach-applications`, payload);
            if (r.data?.application_id && !["honeypot", "too_fast", "rate_limited"].includes(r.data.application_id)) {
                setDone(true);
                toast.success("Application sent — check your inbox for confirmation");
            } else {
                // bot path — silently say ok
                setDone(true);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg || "Validation error" : (detail || "Couldn't submit");
            toast.error("Couldn't submit", { description: msg });
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div data-testid="careers-form-done" className="mt-10 border border-brand-navy-700 rounded-2xl bg-brand-navy-950/40 p-8 text-center">
                <Send className="w-8 h-8 text-brand-red mx-auto mb-3" />
                <h3 className="font-display text-3xl tracking-wider uppercase">Got it.</h3>
                <p className="mt-3 text-zinc-300 text-sm leading-relaxed max-w-md mx-auto">
                    Check your email — we sent you a confirmation. If we're hiring, you'll hear from us within
                    a week or two. If not, you're on file and we'll reach out when something opens up.
                </p>
            </div>
        );
    }

    return (
        <form
            data-testid="careers-form"
            className="mt-8 space-y-8"
            onSubmit={(e) => { e.preventDefault(); submit(); }}
        >
            {/* Role toggle */}
            <div>
                <Label>What are you applying for?</Label>
                <div className="mt-3 grid grid-cols-2 gap-3">
                    {[
                        { id: "coach", label: "Coach", sub: "Full or part-time coaching role" },
                        { id: "internship", label: "Internship", sub: "Learn the floor, earn the room" },
                    ].map((opt) => (
                        <button
                            type="button"
                            key={opt.id}
                            data-testid={`role-${opt.id}`}
                            onClick={() => setRole(opt.id)}
                            className={`text-left border rounded-xl p-4 transition-colors ${
                                role === opt.id
                                    ? "border-brand-red bg-brand-red/10"
                                    : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                            }`}
                        >
                            <div className={`font-display text-2xl tracking-wider uppercase ${role === opt.id ? "text-brand-red" : "text-white"}`}>
                                {opt.label}
                            </div>
                            <div className="text-xs text-zinc-300 mt-1">{opt.sub}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Identity */}
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First name *">
                    <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="form-first-name" className={inp} />
                </Field>
                <Field label="Last name *">
                    <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="form-last-name" className={inp} />
                </Field>
                <Field label="Email *">
                    <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="form-email" className={inp} />
                </Field>
                <Field label="Phone *">
                    <input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="form-phone" className={inp} />
                </Field>
            </div>

            <Field label="LinkedIn URL *">
                <div className="relative">
                    <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <input
                        value={form.linkedin_url}
                        onChange={(e) => set("linkedin_url", e.target.value)}
                        placeholder="https://linkedin.com/in/yourname"
                        data-testid="form-linkedin"
                        className={`${inp} pl-9`}
                    />
                </div>
            </Field>

            <Field label="Resume link (optional)" hint="Google Doc, Dropbox, or any public URL — leave blank if you'd rather we ask later.">
                <input value={form.resume_url} onChange={(e) => set("resume_url", e.target.value)} data-testid="form-resume" className={inp} />
            </Field>

            {/* Role-specific block */}
            {role === "coach" ? (
                <div className="space-y-4">
                    <SectionDivider>Coaching experience</SectionDivider>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Field label="Years coaching">
                            <input type="number" min="0" max="60" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="form-years" className={inp} />
                        </Field>
                        <Field label="Current employer" hint="Optional">
                            <input value={form.current_employer} onChange={(e) => set("current_employer", e.target.value)} data-testid="form-employer" className={inp} />
                        </Field>
                        <Field label="Availability">
                            <select value={form.availability} onChange={(e) => set("availability", e.target.value)} data-testid="form-availability" className={inp}>
                                <option value="">—</option>
                                <option value="full_time">Full time</option>
                                <option value="part_time">Part time</option>
                                <option value="either">Either works</option>
                            </select>
                        </Field>
                    </div>
                    <Field label="Certifications you hold" hint="Tap all that apply">
                        <div className="flex flex-wrap gap-2">
                            {COACH_CERT_OPTIONS.map((c) => {
                                const on = certs.includes(c);
                                return (
                                    <button
                                        type="button"
                                        key={c}
                                        data-testid={`cert-${c}`}
                                        onClick={() => toggleCert(c)}
                                        className={`text-[11px] tracking-wider uppercase px-3 py-1.5 border transition-colors ${
                                            on
                                                ? "border-brand-red bg-brand-red/15 text-white"
                                                : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>
                </div>
            ) : (
                <div className="space-y-4">
                    <SectionDivider>Internship details</SectionDivider>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="School *">
                            <input value={form.school} onChange={(e) => set("school", e.target.value)} data-testid="form-school" className={inp} />
                        </Field>
                        <Field label="Graduation year *">
                            <input type="number" min="2024" max="2035" value={form.grad_year} onChange={(e) => set("grad_year", e.target.value)} data-testid="form-grad-year" className={inp} />
                        </Field>
                        <Field label="Hours per week *">
                            <input type="number" min="1" max="60" value={form.hours_per_week} onChange={(e) => set("hours_per_week", e.target.value)} data-testid="form-hours" className={inp} />
                        </Field>
                        <Field label="Compensation preference">
                            <select value={form.compensation_pref} onChange={(e) => set("compensation_pref", e.target.value)} data-testid="form-comp" className={inp}>
                                <option value="">—</option>
                                <option value="paid">Paid</option>
                                <option value="unpaid">Unpaid OK</option>
                                <option value="credit">School credit</option>
                                <option value="any">Any works</option>
                            </select>
                        </Field>
                    </div>
                </div>
            )}

            <Field label="Earliest start date" hint="Optional — month + year is fine">
                <input value={form.earliest_start} onChange={(e) => set("earliest_start", e.target.value)} placeholder="e.g. June 2026" data-testid="form-start" className={inp} />
            </Field>

            <Field label="Why MaxSC? *" hint="What about us specifically? Be honest — 2-4 sentences.">
                <textarea
                    rows={4}
                    value={form.why_maxsc}
                    onChange={(e) => set("why_maxsc", e.target.value)}
                    data-testid="form-why"
                    className={inp}
                    maxLength={2000}
                />
            </Field>

            {/* Structured intake */}
            <SectionDivider>How you think</SectionDivider>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Not a test — there are no wrong answers. We just want to see how you approach the work.
            </p>

            {philosophyQs.map((q) => (
                <Field key={q.slug} label={q.prompt}>
                    <div className="space-y-2">
                        {q.options.map((opt, i) => {
                            const on = philosophy[q.slug] === opt;
                            return (
                                <button
                                    type="button"
                                    key={`${q.slug}-${i}`}
                                    data-testid={`phil-${q.slug}-${i}`}
                                    onClick={() => setPhilosophy((p) => ({ ...p, [q.slug]: opt }))}
                                    className={`block w-full text-left text-sm leading-relaxed border rounded-lg px-4 py-3 transition-colors ${
                                        on
                                            ? "border-brand-red bg-brand-red/10 text-white"
                                            : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-600"
                                    }`}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                </Field>
            ))}

            {cueOptions.length > 0 && (
                <Field label="Pick the 3 cues you'd most likely give a kid struggling to keep their chest up in a back squat." hint="Pick exactly 3.">
                    <div className="flex flex-wrap gap-2">
                        {cueOptions.map((c) => {
                            const on = cues.includes(c);
                            return (
                                <button
                                    type="button"
                                    key={c}
                                    data-testid={`cue-${c.slice(0, 12)}`}
                                    onClick={() => toggleCue(c)}
                                    className={`text-xs leading-relaxed px-3 py-2 border rounded-lg transition-colors max-w-full ${
                                        on
                                            ? "border-brand-red bg-brand-red/10 text-white"
                                            : "border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-600"
                                    }`}
                                >
                                    {c}
                                </button>
                            );
                        })}
                    </div>
                </Field>
            )}

            <Field label="Walk us through a first session with a new 14-year-old basketball athlete." hint="150 words max. Show us how you think, not how you write.">
                <textarea
                    rows={5}
                    value={form.scenario_response}
                    onChange={(e) => set("scenario_response", e.target.value)}
                    data-testid="form-scenario"
                    className={inp}
                    maxLength={1500}
                />
            </Field>

            {/* Honeypot — hidden from humans */}
            <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                aria-hidden="true"
                className="absolute opacity-0 pointer-events-none -left-[9999px] w-px h-px"
                name="company"
            />

            <div className="pt-4 flex flex-wrap items-center justify-between gap-4">
                <p className="text-[11px] text-zinc-500 max-w-sm leading-relaxed">
                    By submitting you agree to be contacted by Maximum Sports Conditioning about your application.
                </p>
                <button
                    type="submit"
                    data-testid="form-submit"
                    disabled={busy || !canSubmit}
                    className="btn-tactical disabled:opacity-50"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send application
                </button>
            </div>
        </form>
    );
}

// -------------------------- Form helpers --------------------------

const inp = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-brand-red";

function Label({ children }) {
    return <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-200">{children}</div>;
}

function Field({ label, hint, children }) {
    return (
        <div>
            <Label>{label}</Label>
            {children && <div className="mt-1.5">{children}</div>}
            {hint && <div className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">{hint}</div>}
        </div>
    );
}

function SectionDivider({ children }) {
    return (
        <div className="pt-4 flex items-center gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">{children}</div>
            <div className="flex-1 h-px bg-zinc-900" />
        </div>
    );
}
